import * as React from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  ContractsContractVersionChange,
  ContractsEmployeesContractVersionFragment,
  JobCatalogTreeLevelNodeFragment,
} from 'generated/resources/graphql'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { isCurrentYear } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import MetadataInfo from 'modules/contracts/components/ContractHistory/ContractVersionCard/MetadataInfo'
import StatusTag from 'modules/contracts/components/ContractHistory/ContractVersionCard/StatusTag'
import ContractVersionUpdates from 'modules/contracts/components/ContractHistory/ContractVersionUpdates'
import { Milestone } from 'modules/contracts/components/ContractHistory/Timeline/Milestone'
import getContractVersionStyles from 'modules/contracts/components/ContractHistory/lib/getContractVersionStyles'
import { getMilestoneColor } from 'modules/contracts/components/ContractHistory/lib/timeline'
import { FLOWS } from 'modules/contracts/components/FlowSelectorModal/constants'
import {
  ContractSchedule,
  getContractVersionSchedule,
} from 'modules/contracts/lib/graphqlContractVersionSchedule'
import { useRoute } from 'modules/contracts/pages/ContractVersionsPage/_RouteContext'

interface Props {
  contractVersion: ContractsEmployeesContractVersionFragment
  contractVersions: ContractsEmployeesContractVersionFragment[]
  contractVersionChanges: ContractsContractVersionChange | undefined | null
  setSelectedGraphqlContractVersion: (contractVersionId: number | undefined) => void
  highlightedItemId: number | undefined
  hasPreviousContract: boolean
  hasDevJobCatalogTree: boolean
  hasTimeTrackingAnnualisationDailyValue: boolean
  hasDevTimeTrackingWorkableDaysDefinition: boolean
  jobCatalogTreeLevelNodes?: JobCatalogTreeLevelNodeFragment[]
  hasCompensationsManagement: boolean
}

const ContractVersionCard = ({
  contractVersion,
  contractVersions,
  contractVersionChanges,
  highlightedItemId,
  setSelectedGraphqlContractVersion,
  hasPreviousContract,
  hasDevJobCatalogTree,
  hasTimeTrackingAnnualisationDailyValue,
  hasDevTimeTrackingWorkableDaysDefinition,
  hasCompensationsManagement,
  jobCatalogTreeLevelNodes,
}: Props) => {
  const route = useRoute()
  const [isHovered, setIsHovered] = React.useState(false)
  const isHighlighted = highlightedItemId === contractVersion.id
  const contractVersionSchedule = getContractVersionSchedule(contractVersion, contractVersions)
  const { background, borderStyle, borderColor } = getContractVersionStyles(
    contractVersionSchedule,
    isHovered,
    isHighlighted
  )
  const [isExpanded, toggleIsExpanded] = React.useState<boolean>(
    contractVersionSchedule === ContractSchedule.ongoing || isHighlighted
  )

  const getContractTitle = () => {
    if (!hasPreviousContract) return i18n.t('contract_versions.navigator.timeline.hired')

    if (contractVersion.actionType === FLOWS.promotion) {
      let contractTitle = i18n.t('contract_versions.navigator.timeline.promotion')

      if (contractVersionChanges) {
        const jobTitleUpdate = contractVersionChanges.updates.find(
          (update) => update.field === 'job_title'
        )

        if (jobTitleUpdate) {
          contractTitle = i18n.t('contract_versions.navigator.timeline.promotion_with_title', {
            newJobTitle: jobTitleUpdate.newValue,
          })
        }
      }

      return contractTitle
    }

    const contractType = ''

    // Handle updated contract logic (from CollapsibleContractVersionCard)
    if (contractVersion.actionType && contractVersion.actionType !== FLOWS.promotion) {
      return contractType === ''
        ? i18n.t('contract_versions.navigator.timeline.updated_contract')
        : i18n.t('contract_versions.navigator.timeline.contract_type', {
            type: contractType,
          })
    }

    // Handle initial hire logic (from FirstContractVersionCard)
    if (contractType === '') return i18n.t('contract_versions.navigator.timeline.hired')

    return i18n.t('contract_versions.navigator.timeline.hired_with_type', {
      contractType: contractType,
    })
  }

  const onContractVersionSelected = (cv: ContractsEmployeesContractVersionFragment) => {
    if (!cv.id) return

    const showChanges = isHighlighted ? !isExpanded : true
    window.history.replaceState(null, 'Factorial', route.contractVersionPath(cv.id))
    toggleIsExpanded(showChanges)
    setSelectedGraphqlContractVersion(cv.id)
  }

  const effectiveOnDate = stringToDate(contractVersion.effectiveOn, 'backend')
  const date = isCurrentYear(effectiveOnDate)
    ? i18nDateToString(effectiveOnDate, 'ui_dm_long')
    : i18nDateToString(effectiveOnDate, 'ui_dmy_long')

  const createdAtDate = stringToDate(contractVersion.createdAt, 'backend_datetime')
  const createdAtDateString = i18nDateToString(createdAtDate, 'ui_date_day_month_year')

  return (
    <Box
      paddingX="s16"
      paddingTop="s16"
      paddingBottom="s16"
      background={background}
      gap="s24"
      width="full"
      cursor="pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onContractVersionSelected(contractVersion)}
      borderRadius={{ all: 'abs008' }}
      border={{
        all: {
          width: 's1',
          style: borderStyle,
          color: borderColor,
        },
      }}
      style={{ position: 'relative' }}
    >
      <Box gap="s2" width="full">
        <Milestone
          color={getMilestoneColor(contractVersionSchedule)}
          label={date}
          extraPadding={17}
        >
          <Box flexDirection="column" width="full" gap="s8">
            <Box flexDirection="row" alignItems="flexStart" justifyContent="spaceBetween" gap="s8">
              <Box flexDirection="row" alignItems="center" gap="s8">
                <Icon.Small icon={isExpanded ? 'chevron-up' : 'chevron-down'} color="grey800" />
                <Text.Normal weight="semibold" size="200" color="grey1000">
                  {getContractTitle()}
                </Text.Normal>
              </Box>
              {contractVersionSchedule !== ContractSchedule.expired && (
                <StatusTag schedule={contractVersionSchedule} isHighlighted={isHighlighted} />
              )}
            </Box>
            {contractVersion.createdByName && (
              <MetadataInfo
                authorName={contractVersion.createdByName}
                createdAt={createdAtDateString}
                authorAvatarUrl={contractVersion.createdByAvatar}
              />
            )}
          </Box>
        </Milestone>
        {isExpanded && contractVersionChanges && contractVersion.id && (
          <ContractVersionUpdates
            contractVersionId={contractVersion.id}
            updates={contractVersionChanges.updates}
            legalEntityId={contractVersion.employee?.legalEntity?.id || 0}
            country={contractVersion.country || ''}
            hasDevJobCatalogTree={hasDevJobCatalogTree}
            hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
            hasDevTimeTrackingWorkableDaysDefinition={hasDevTimeTrackingWorkableDaysDefinition}
            jobCatalogTreeLevelNodes={jobCatalogTreeLevelNodes}
            hasCompensationsManagement={hasCompensationsManagement}
          />
        )}
      </Box>
    </Box>
  )
}

export default ContractVersionCard
